import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao } from "../ui";
import { listaDeRedes, usePoliticaDaOrganizacao, useSalvarPolitica } from "../../nucleo/acesso";
import type { PoliticaDeSeguranca } from "../../nucleo/acesso";
import { mensagemDoConsole } from "../../nucleo/console";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

/**
 * A política de acesso de uma organização: MFA obrigatório e a lista de redes permitidas.
 *
 * Quem não pode gerenciar nem chega a ver isto (a tela esconde, o servidor nega). A trava de
 * autoexclusão é do servidor: uma lista que deixasse o próprio administrador de fora é recusada
 * com o endereço dele na mensagem.
 */
export function PoliticaDaOrganizacao({ organizacaoId }: { organizacaoId: string }) {
  const politica = usePoliticaDaOrganizacao(organizacaoId);

  if (politica.isPending) return <p role="status">Carregando a política de acesso…</p>;
  if (politica.isError) return <Aviso tom="erro">{mensagemDoConsole(politica.error)}</Aviso>;
  return <FormularioDaPolitica organizacaoId={organizacaoId} politica={politica.data} />;
}

function FormularioDaPolitica({
  organizacaoId,
  politica,
}: {
  organizacaoId: string;
  politica: PoliticaDeSeguranca;
}) {
  const salvar = useSalvarPolitica(organizacaoId);
  const voltarParaHome = useVoltarParaHome();
  const [mfa, setMfa] = useState(politica.mfa_obrigatorio);
  const [redes, setRedes] = useState(politica.ips_permitidos.join("\n"));

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    salvar.mutate(
      { mfa_obrigatorio: mfa, ips_permitidos: listaDeRedes(redes) },
      {
        // O servidor normaliza (`10.0.0.1` vira `10.0.0.1/32`, duplicata some): a tela passa a
        // mostrar o que ficou valendo, não o que foi digitado.
        onSuccess: (salva) => {
          setMfa(salva.mfa_obrigatorio);
          setRedes(salva.ips_permitidos.join("\n"));
          voltarParaHome("Política de acesso salva.");
        },
      },
    );
  }

  return (
    <form onSubmit={enviar} noValidate data-testid="politica-da-organizacao">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={mfa}
          onChange={(evento) => setMfa(evento.target.checked)}
          data-testid="mfa-obrigatorio"
        />
        <span>
          <b>Exigir a verificação em duas etapas</b> de todas as pessoas desta organização. Quem
          ainda não configurou é levado a configurar na próxima entrada.
        </span>
      </label>

      <label className="campo__rotulo mt-4" htmlFor="redes-permitidas">
        Redes permitidas
      </label>
      <textarea
        id="redes-permitidas"
        className="campo__controle redes-permitidas"
        value={redes}
        onChange={(evento) => setRedes(evento.target.value)}
        placeholder={"203.0.113.0/24\n198.51.100.7"}
        data-testid="redes-permitidas"
      />
      <p className="campo__ajuda">
        Um endereço ou rede (CIDR) por linha. Vazio = sem restrição. Com a lista preenchida, o
        acesso de qualquer endereço fora dela é recusado — no login e a cada pedido.
        {politica.seu_ip ? (
          <>
            {" "}
            Você está acessando de <b data-testid="seu-ip">{politica.seu_ip}</b>.
          </>
        ) : null}
      </p>

      {salvar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-da-politica">
          {mensagemDoConsole(salvar.error)}
        </Aviso>
      ) : null}
      <Botao type="submit" disabled={salvar.isPending} data-testid="salvar-politica">
        {salvar.isPending ? "Salvando…" : "Salvar política"}
      </Botao>
    </form>
  );
}
